import React from 'react';

import {PartJSON} from 'biojs/Part';
import {SequenceSubtype} from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import {HoistedTag} from 'inventory/InventoryFileConstants';
import {isOligoSubtype} from 'molbio/SeqUtil';
import {BlobLinkPreviewTabProvider} from 'notebook/data-tables/BlobLinkPreviewTabContext';
import {confirmExpirationDateIfEdited} from 'samples/valid-contents/ConfirmEntitiesExpirationDate';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import * as IntegrationTagUtil from 'schemas/tags/integration/IntegrationTagUtil';
import {getTagTableFriendlyTags} from 'schemas/tags/TagUtil';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import ErrorIcon from 'ui-library/icons/ErrorIcon';

import AutoFillLink from './AutoFillLink';
import MetadataTagTable from './MetadataTagTable';
import SchemaTagTableSchemaSelector from './SchemaTagTableSchemaSelector';

interface SchemaTableTitle {
  hasRegistrySupport: boolean;
  tagSchema: TagSchema | null;
}

function SchemaTableTitle({hasRegistrySupport, tagSchema}: SchemaTableTitle): JSX.Element {
  return (
    <div>
      Schema
      {hasRegistrySupport && !tagSchema && <ErrorIcon className="u-mini-spacing-left" />}
    </div>
  );
}

export interface SchemaTagTableProps {
  sequenceSubtype?: SequenceSubtype;
  errorsByFieldId: {[fieldId: number]: Array<SchemaValidationErrorRecord>};
  fileId: string;
  fileType?: string;
  hasRegistrySupport: boolean;
  hoistedTags: Array<HoistedTag>;
  isFileRegistered: boolean;
  areTagsEditable: boolean;
  isSchemaEditable: boolean;
  isLoadingTags: boolean;
  onAnalyzeProteins: ((proteinIds: Array<string>) => void) | null;
  onUpdateTags?: (
    inputData: TagUpdateOptions,
    param?: {onFailure: () => void}
  ) => Promise<Array<{id: string; tags: Array<Tag>}> | null>;
  onUpdateTagSchema: (tagSchema: TagSchema | null) => void;
  schemaTags: Array<SchemaTag>;
  sequenceParts?: Array<PartJSON> | null;
  tagSchema: TagSchema | null;
}

export default function SchemaTagTable(props: SchemaTagTableProps): JSX.Element {
  const tagTableFriendlyTags = getTagTableFriendlyTags(props.schemaTags);

  async function handleUpdateTags(
    inputData: TagUpdateOptions,
    param?: {onFailure: () => void}
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> {
    if (props.tagSchema) {
      const {shouldProceed} = await confirmExpirationDateIfEdited(
        props.tagSchema.id,
        inputData,
        props.fileId
      );
      if (!shouldProceed) {
        param?.onFailure();
        return Promise.resolve(null);
      }
    }
    if (!props.onUpdateTags) {
      return Promise.resolve(null);
    }
    return props.onUpdateTags(inputData, param);
  }

  return (
    <BlobLinkPreviewTabProvider fileId={props.fileId}>
      <MetadataTagTable
        canRefreshTags={
          ComputedTagUtil.hasComputedTags(tagTableFriendlyTags) ||
          IntegrationTagUtil.hasIntegrationTags(tagTableFriendlyTags)
        }
        emptyMessage="This schema has no fields."
        errorsByFieldId={props.errorsByFieldId}
        fileId={props.fileId}
        hoistedTags={props.hoistedTags}
        hoistedTagSchemaGraphs={props.tagSchema ? props.tagSchema.hoistedTagSchemaGraphs : []}
        initialTags={tagTableFriendlyTags}
        areTagsEditable={props.areTagsEditable}
        isLoadingTags={props.isLoadingTags}
        onAnalyzeProteins={props.onAnalyzeProteins}
        sequenceParts={props.sequenceParts}
        subheaderNode={
          <SchemaTagTableSchemaSelector
            sequenceSubtype={props.sequenceSubtype}
            fileType={props.fileType ?? TagSchemaUtil.schemaTagFolderTypeForFileId(props.fileId)}
            isSchemaEditable={props.isSchemaEditable}
            isFileRegistered={props.isFileRegistered}
            onUpdateTagSchema={props.onUpdateTagSchema}
            tagSchema={props.tagSchema}
          />
        }
        title={<SchemaTableTitle hasRegistrySupport={props.hasRegistrySupport} tagSchema={props.tagSchema} />}
        updateTags={handleUpdateTags}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        autofillBanner={(isEditing: boolean) => (
          <AutoFillLink
            isEditing={isEditing}
            isOligo={isOligoSubtype(props.sequenceSubtype)}
            fileId={props.fileId}
            areTagsEditable={props.areTagsEditable}
            tagSchema={props.tagSchema}
          />
        )}
      />
    </BlobLinkPreviewTabProvider>
  );
}
